import { useState, useEffect } from "react";
import { Grid2, TextField, Button, Typography } from "@mui/material";
import useLanguage from "../../../hooks/useLanguage";
import useContent from "../../../hooks/useContent";
import useToaster from "../../../hooks/useToaster";

const MAX_PAD = 60;
const MAX_CLIP_PAD = 30;
const MIN_GUIDE_REFRESH = 1;
const MAX_GUIDE_REFRESH = 720;

export default function RecordingTab() {
  const [
    {
      pages: { settings: text },
    },
  ] = useLanguage();
  const { getRecordingSettings, updateRecordingSettings } = useContent();
  const { addToaster } = useToaster();

  const [padMinutes, setPadMinutes] = useState("");
  const [clipPadSeconds, setClipPadSeconds] = useState("");
  const [guideRefreshMinutes, setGuideRefreshMinutes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getRecordingSettings()
      .then((s) => {
        if (s && typeof s.padMinutes === "number") setPadMinutes(String(s.padMinutes));
        if (s && typeof s.clipPadSeconds === "number") setClipPadSeconds(String(s.clipPadSeconds));
        if (s && typeof s.guideRefreshMinutes === "number")
          setGuideRefreshMinutes(String(s.guideRefreshMinutes));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const n = Number(padMinutes);
    const c = Number(clipPadSeconds);
    const g = Number(guideRefreshMinutes);
    if (!Number.isFinite(n) || n < 0 || n > MAX_PAD) {
      addToaster({ message: text.errors.invalidPadding, severity: "warning" });
      return;
    }
    if (!Number.isFinite(c) || c < 0 || c > MAX_CLIP_PAD) {
      addToaster({ message: text.errors.invalidClipPadding, severity: "warning" });
      return;
    }
    if (!Number.isFinite(g) || g < MIN_GUIDE_REFRESH || g > MAX_GUIDE_REFRESH) {
      addToaster({ message: text.errors.invalidGuideRefresh, severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const saved = await updateRecordingSettings({
        padMinutes: Math.round(n),
        clipPadSeconds: Math.round(c),
        guideRefreshMinutes: Math.round(g),
      });
      setPadMinutes(String(saved.padMinutes));
      setClipPadSeconds(String(saved.clipPadSeconds));
      setGuideRefreshMinutes(String(saved.guideRefreshMinutes));
      addToaster({ message: text.success.saved, severity: "success" });
    } catch {
      addToaster({ message: text.errors.saveFailed, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid2 container spacing={2.5} sx={{ maxWidth: 400 }}>
      <Grid2 size={12}>
        <Typography variant="subtitle1" fontWeight={600}>
          {text.recording.heading}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {text.recording.padHelp}
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <TextField
          type="number"
          label={text.recording.padLabel}
          value={padMinutes}
          onChange={(e) => setPadMinutes(e.target.value)}
          disabled={loading || saving}
          inputProps={{ min: 0, max: MAX_PAD }}
          size="small"
          fullWidth
        />
      </Grid2>
      <Grid2 size={12}>
        <TextField
          type="number"
          label={text.recording.clipPadLabel}
          helperText={text.recording.clipPadHelp}
          value={clipPadSeconds}
          onChange={(e) => setClipPadSeconds(e.target.value)}
          disabled={loading || saving}
          inputProps={{ min: 0, max: MAX_CLIP_PAD }}
          size="small"
          fullWidth
        />
      </Grid2>
      <Grid2 size={12}>
        <TextField
          type="number"
          label={text.recording.guideRefreshLabel}
          helperText={text.recording.guideRefreshHelp}
          value={guideRefreshMinutes}
          onChange={(e) => setGuideRefreshMinutes(e.target.value)}
          disabled={loading || saving}
          inputProps={{ min: MIN_GUIDE_REFRESH, max: MAX_GUIDE_REFRESH }}
          size="small"
          fullWidth
        />
      </Grid2>
      <Grid2 size={12}>
        <Button variant="contained" onClick={save} disabled={loading || saving}>
          {text.save}
        </Button>
      </Grid2>
    </Grid2>
  );
}
